import React, { useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import {
	Select,
	MenuItem,
	InputBase,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	Typography,
	DialogActions,
	Button,
	Pagination,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { mdiRefresh, mdiDownload, mdiCalendarMonth, mdiClose } from "@mdi/js";
import styles from "./styles.module.scss";
import { clsx } from "@ismael1361/utils";
import { SystemLogReportDTO } from "shared/types";
import { HandleError } from "shared/utils";
import { APIClientServices, NotistackServices } from "shared/services";

export const SystemLogsPage: React.FC = () => {
	const [logsData, setLogsData] = useState<SystemLogReportDTO>({
		logs: [],
		pagination: {
			filter: {
				offset: 0,
				limit: 10,
			},
			current: 1,
			total: 0,
			pageSize: 20,
			hasNextPage: false,
		},
		stats: {
			activeSessions: 0,
			errorRate: 0,
			avgLatency: 0,
		},
	});
	const [loading, setLoading] = useState(true);
	const [datePickerOpen, setDatePickerOpen] = useState(false);
	const [statusFilter, setStatusFilter] = useState<"ALL" | "SUCCESS" | "ERROR" | "WARNING">("ALL");
	const [levelFilter, setLevelFilter] = useState<"INFO" | "WARN" | "ERROR" | "DEBUG" | "CRITICAL" | "ALL">("ALL");
	const [dateFilter, setDateFilter] = useState<Date>(new Date());
	const [selectedLog, setSelectedLog] = useState<SystemLogReportDTO["logs"][0] | null>(null);
	const [page, setPage] = useState(1);
	const time = useRef<NodeJS.Timeout>(undefined);

	const handleReplayLogs = () => {
		setLoading(true);
		clearTimeout(time.current);

		time.current = setTimeout(async () => {
			try {
				const data = await APIClientServices.get<SystemLogReportDTO>("v1/backoffice/system-logs", {
					"dateFrom": dayjs(dateFilter).startOf("day").toISOString(),
					"dateTo": dayjs(dateFilter).endOf("day").toISOString(),
					"logLevel": levelFilter === "ALL" ? "" : levelFilter,
					"source": "",
					"page[offset]": (page - 1) * 20,
					"page[limit]": 20,
				});

				data.logs = data.logs.map((log) => ({
					...log,
					source:
						log.source && log.source.trim() !== "" && log.source.toLocaleLowerCase() !== "undefined" && !/\s/gi.test(log.source.trim())
							? log.source
							: "???",
					time: new Date(log.time),
				}));

				setLogsData(data);
			} catch (error) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Failed to fetch logs");
			} finally {
				setLoading(false);
			}
		}, 1000);
	};

	const handleExportLogs = () => {
		const blob = new Blob([JSON.stringify(logsData.logs, null, 2)], { type: "application/json" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `system_logs_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.json`;
		a.click();
		URL.revokeObjectURL(url);
	};

	useEffect(() => {
		const time = setTimeout(() => {
			handleReplayLogs();
		}, 500);

		return () => clearTimeout(time);
	}, [statusFilter, levelFilter, dateFilter, page]);

	return (
		<>
			<div className={styles.container}>
				<div className={styles.pageHeader}>
					<div>
						<h2>Logs de Atividade do Sistema</h2>
						<p>Monitoramento em tempo real de requisições, erros e eventos globais.</p>
					</div>
					<div className={styles.headerActions}>
						<button
							className={clsx(styles.btn, styles.btnSecondary)}
							onClick={() => handleReplayLogs()}
							disabled={loading}
						>
							<Icon
								path={mdiRefresh}
								size={0.7}
							/>
							Replay Logs
						</button>
						<button
							className={clsx(styles.btn, styles.btnPrimary)}
							onClick={handleExportLogs}
							disabled={loading}
						>
							<Icon
								path={mdiDownload}
								size={0.7}
							/>
							Exportar JSON
						</button>
					</div>
				</div>

				<div className={styles.filtersToolbar}>
					<div className={clsx(styles.filterGroup, { [styles.disabled]: loading })}>
						<span className={styles.label}>Status:</span>
						<Select
							className={styles.select}
							variant="standard"
							disableUnderline
							defaultValue="ALL"
							disabled={loading}
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value as any)}
						>
							<MenuItem value="ALL">Todos os Status</MenuItem>
							<MenuItem value="SUCCESS">Success</MenuItem>
							<MenuItem value="ERROR">Error</MenuItem>
							<MenuItem value="WARNING">Warning</MenuItem>
						</Select>
					</div>
					<div className={clsx(styles.filterGroup, { [styles.disabled]: loading })}>
						<span className={styles.label}>Nível:</span>
						<Select
							className={styles.select}
							variant="standard"
							disableUnderline
							defaultValue="Todos os Níveis"
							disabled={loading}
							value={levelFilter}
							onChange={(e) => setLevelFilter(e.target.value as any)}
						>
							<MenuItem value="ALL">Todos os Níveis</MenuItem>
							<MenuItem value="INFO">Info</MenuItem>
							<MenuItem value="WARNING">Warning</MenuItem>
							<MenuItem value="ERROR">Error</MenuItem>
							<MenuItem value="DEBUG">Debug</MenuItem>
							<MenuItem value="CRITICAL">Critical</MenuItem>
						</Select>
					</div>
					<div className={clsx(styles.filterGroup, styles.disabled)}>
						<span className={styles.label}>Provedor:</span>
						<Select
							className={styles.select}
							variant="standard"
							disableUnderline
							defaultValue="ALL"
							disabled={loading}
						>
							<MenuItem value="ALL">Todos Provedores</MenuItem>
							<MenuItem value="OpenAI">OpenAI</MenuItem>
							<MenuItem value="Anthropic">Anthropic</MenuItem>
							<MenuItem value="Google Vertex">Google Vertex</MenuItem>
							<MenuItem value="Internal Systems">Internal Systems</MenuItem>
						</Select>
					</div>
					<div className={clsx(styles.filterGroup, { [styles.disabled]: loading })}>
						<span className={styles.label}>Período:</span>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<div
								className={clsx(styles.dateInputContainer)}
								onClick={datePickerOpen ? undefined : () => setDatePickerOpen(true)}
							>
								<Icon
									path={mdiCalendarMonth}
									size={0.6}
									className={styles.dateIcon}
								/>
								<DatePicker
									open={datePickerOpen}
									onClose={() => setDatePickerOpen(false)}
									defaultValue={dayjs("2023-10-26")}
									value={dayjs(dateFilter)}
									onAccept={(date) => {
										setDatePickerOpen(false);
										if (date) {
											setDateFilter(date.toDate());
										}
									}}
									format="YYYY/MM/DD"
									slotProps={{
										textField: {
											variant: "standard",
											className: styles.dateInput,
											InputProps: {
												disableUnderline: true,
												readOnly: true,
											},
										},
										openPickerButton: {
											style: { display: "none" },
											disabled: loading,
										},
									}}
								/>
							</div>
						</LocalizationProvider>
					</div>

					<div className={styles.liveFeed}>
						<div className={styles.liveBadge}>
							<span className={styles.dot}></span>
							<span>Live Feed</span>
						</div>
					</div>
				</div>

				<div className={styles.logsContent}>
					<div className={styles.tableContainer}>
						<table>
							<thead>
								<tr>
									<th>Timestamp</th>
									<th>Level</th>
									<th>Source</th>
									<th>Message</th>
									<th>Status</th>
									<th>Duration</th>
								</tr>
							</thead>
							<tbody>
								{loading && (
									<tr>
										<td
											className={styles.loadingCell}
											colSpan={6}
											style={{
												pointerEvents: "none",
												userSelect: "none",
											}}
										>
											<div className={styles.loadingRow}>
												<CircularProgress
													color="inherit"
													size={16}
												/>{" "}
												<span>Carregando logs...</span>
											</div>
										</td>
									</tr>
								)}
								{!loading && logsData.logs.length === 0 && (
									<tr>
										<td
											colSpan={6}
											className={clsx(styles.textSubtle, styles.noLogsCell)}
											style={{
												justifyContent: "center",
												pointerEvents: "none",
												userSelect: "none",
												fontStyle: "italic",
												fontSize: "1rem",
												padding: "2rem 1rem",
											}}
										>
											Nenhum log encontrado.
										</td>
									</tr>
								)}
								{logsData?.logs?.map((log) => {
									const id = [log.time.getTime(), log.level, log.source].join("-");

									const level = log.level.toUpperCase() as
										| "ERROR"
										| "WARN"
										| "INFO"
										| "DEBUG"
										| "TRACE"
										| "CRITICAL"
										| "ALERT"
										| "EMERGENCY";

									const status = (HandleError.codes as any)[log.statusCode] ?? "Unknown Status";

									const statusCode: "Error" | "Warning" | "Success" | "None" =
										log.statusCode >= 500
											? "Error"
											: log.statusCode >= 400
												? "Warning"
												: log.statusCode <= 0
													? "None"
													: "Success";

									const message = log.message.length > 100 ? log.message.substring(0, 100) + "..." : log.message;

									return (
										<tr
											key={id}
											className={clsx(level === "ERROR" && styles.bgDanger, level === "WARN" && styles.bgWarning)}
											onClick={() => setSelectedLog(log)}
											style={{ cursor: "pointer" }}
										>
											<td
												data-label="Timestamp"
												className={styles.textSubtle}
												style={{ whiteSpace: "nowrap" }}
											>
												<span>{log.time.toLocaleString()}</span>
											</td>
											<td
												data-label="Level"
												className={clsx(
													level === "CRITICAL" || level === "ERROR"
														? styles.textError
														: level === "WARN"
															? styles.textWarning
															: level === "DEBUG"
																? styles.textSubtle
																: styles.textInfo,
												)}
											>
												<span>{level}</span>
											</td>
											<td
												data-label="Source"
												style={{ whiteSpace: "nowrap" }}
											>
												<span>{log.source}</span>
											</td>
											<td data-label="Message">
												<span style={{ wordBreak: "break-all" }}>
													{message.split(/\\n/gi).map((line, index) => (
														<p key={index}>
															{line}
															<br />
														</p>
													))}
												</span>
											</td>
											<td
												data-label="Status"
												className={clsx(
													statusCode === "Error"
														? styles.textError
														: statusCode === "Warning"
															? styles.textWarning
															: statusCode === "Success"
																? styles.textSuccess
																: styles.textSubtle,
												)}
												style={{ whiteSpace: "nowrap" }}
											>
												<span>{status}</span>
											</td>
											<td
												data-label="Duration"
												className={styles.textSubtle}
											>
												<span>{log.duration}</span>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<footer className={styles.pageFooter}>
				<Pagination
					count={Math.ceil(logsData.pagination.total / logsData.pagination.pageSize) || 1}
					page={page}
					onChange={(_, p) => setPage(p)}
					shape="rounded"
					size="small"
					disabled={loading}
					sx={{
						"& .MuiPaginationItem-root": {
							"fontFamily": "'JetBrains Mono', monospace",
							"fontSize": "10px",
							"color": "var(--mui-palette-text-secondary)",
							"&.Mui-selected": {
								backgroundColor: "rgba(var(--mui-palette-primary-mainChannel) / 0.1)",
								color: "var(--mui-palette-primary-main)",
								fontWeight: "bold",
								border: "1px solid rgba(var(--mui-palette-primary-mainChannel) / 0.3)",
							},
						},
					}}
				/>
				<div className={styles.stats}>
					<div className={styles.statItem}>
						<span className={styles.label}>ACTIVE SESSIONS:</span>
						<span className={styles.value}>{logsData?.stats?.activeSessions ?? 0}</span>
					</div>
					<div className={styles.statItem}>
						<span className={styles.label}>ERROR RATE (1H):</span>
						<span className={clsx(styles.value, styles.danger)}>{((logsData?.stats?.errorRate ?? 0) * 100).toFixed(2)}%</span>
					</div>
					<div className={styles.statItem}>
						<span className={styles.label}>AVG LATENCY:</span>
						<span className={clsx(styles.value, styles.primary)}>{logsData?.stats?.avgLatency ?? 0}ms</span>
					</div>
				</div>
			</footer>

			<Dialog
				open={!!selectedLog}
				onClose={() => setSelectedLog(null)}
				maxWidth="md"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "var(--mui-palette-background-paper)",
						color: "var(--mui-palette-text-primary)",
						fontFamily: "'JetBrains Mono', monospace",
					},
				}}
			>
				<DialogTitle
					sx={{
						m: 0,
						p: 2,
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						borderBottom: "1px solid var(--mui-palette-divider)",
					}}
				>
					<Typography
						variant="h6"
						component="div"
						style={{ fontFamily: "inherit", fontWeight: "bold" }}
					>
						Detalhes do Log
					</Typography>
				</DialogTitle>
				<DialogContent dividers>
					{selectedLog && (
						<div className={styles.logDetailsContainer}>
							<div className={styles.detailRow}>
								<span className={styles.detailLabel}>Timestamp:</span>
								<span className={styles.detailValue}>{selectedLog.time.toLocaleString()}</span>
							</div>
							<div className={styles.detailRow}>
								<span className={styles.detailLabel}>Level:</span>
								<span
									className={clsx(
										styles.detailValue,
										selectedLog.level === "ERROR" || selectedLog.level === "CRITICAL"
											? styles.textError
											: selectedLog.level === "WARN"
												? styles.textWarning
												: selectedLog.level === "DEBUG"
													? styles.textSubtle
													: styles.textInfo,
									)}
									style={{ textTransform: "uppercase", fontWeight: "bold" }}
								>
									{selectedLog.level}
								</span>
							</div>
							<div className={styles.detailRow}>
								<span className={styles.detailLabel}>Source:</span>
								<span className={styles.detailValue}>{selectedLog.source || "N/A"}</span>
							</div>
							<div className={styles.detailRow}>
								<span className={styles.detailLabel}>Status Code:</span>
								<span className={styles.detailValue}>
									{selectedLog.statusCode} - {(HandleError.codes as any)[selectedLog.statusCode] ?? "Unknown Status"}
								</span>
							</div>
							<div className={styles.detailRow}>
								<span className={styles.detailLabel}>Duration:</span>
								<span className={styles.detailValue}>{selectedLog.duration}</span>
							</div>

							<div className={styles.detailSection}>
								<span className={styles.detailLabel}>Message:</span>
								<pre className={styles.codeBlock}>
									{selectedLog.message.split(/\\n/gi).map((line, index) => (
										<p key={index}>
											{line}
											<br />
										</p>
									))}
								</pre>
							</div>

							{selectedLog.meta && Object.keys(selectedLog.meta).length > 0 && (
								<div className={styles.detailSection}>
									<span className={styles.detailLabel}>Metadata:</span>
									<pre className={styles.codeBlock}>
										{selectedLog.meta.split(/\\n/gi).map((line, index) => (
											<p key={index}>
												{line}
												<br />
											</p>
										))}
									</pre>
								</div>
							)}
						</div>
					)}
				</DialogContent>
				<DialogActions
					sx={{
						borderTop: "1px solid var(--mui-palette-divider)",
						padding: "16px",
					}}
				>
					<Button
						onClick={() => setSelectedLog(null)}
						color="primary"
						variant="contained"
						style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: "bold" }}
					>
						Fechar
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
